import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import debounce from 'debounce-promise';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
import { commonProps, customStyles } from './CustomComponents.js';
import { matchesSelectFilter } from './matchesSelectFilter.js';
import { OptionMarkedLabel } from './OptionMarkedLabel.js';
import { SAsyncCreatableSelect, StyledSelect, SCreatableSelect, SAsyncSelect } from './styled.js';
import { useFixedOptions } from './useFixedOptions.js';
import { useMergeComponents } from './useMergeComponents.js';
import { useSearchHint } from './useSearchHint.js';
export { default as TagInput } from './TagInput.js';
const mergeStyles = (styles) => ({
    ...customStyles,
    ...styles,
});
const debounceLoadOptions = (loadOptions) => loadOptions ? debounce(loadOptions, 300) : undefined;
// the `ref` should be passed using `innerRef` custom prop & not with `forwardRef` because typescript
// currently doesn't support hoc generic types inference well. therefore it's not possible to create a
// generic component using `forwardRef`
//
// const customFilter is used to support EN/DE localization
export const Select = ({ components, innerRef, formatOptionLabel, styles, ...props }) => {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    const customFilter = React.useCallback((option, searchText) => matchesSelectFilter(option, searchText, formatOptionLabel), [formatOptionLabel]);
    const mergedComponents = useMergeComponents(components);
    const searchHintProps = useSearchHint(props);
    const mergedStyles = React.useMemo(() => mergeStyles(styles), [styles]);
    return (_jsx(StyledSelect, { styles: mergedStyles, filterOption: (option, inputValue) => customFilter(option.data, inputValue), ref: innerRef, formatOptionLabel: formatOptionLabel, ...commonProps, ...props, ...searchHintProps, components: mergedComponents, "data-testid": 'styled-select' }));
};
export const StyledAsyncCreatableSelect = ({ components, innerRef, loadOptions, styles, ...props }) => {
    const mergedComponents = useMergeComponents(components);
    const searchHintProps = useSearchHint(props);
    const mergedStyles = React.useMemo(() => mergeStyles(styles), [styles]);
    return (_jsx(SAsyncCreatableSelect, { styles: mergedStyles, ref: innerRef, loadOptions: React.useMemo(() => debounceLoadOptions(loadOptions), [loadOptions]), ...commonProps, ...props, ...searchHintProps, components: mergedComponents }));
};
export const AsyncCreatableMultiSelect = ({ components, innerRef, loadOptions, styles, ...props }) => {
    const mergedComponents = useMergeComponents(components);
    const searchHintProps = useSearchHint(props);
    const mergedStyles = React.useMemo(() => mergeStyles(styles), [styles]);
    return (_jsx(SAsyncCreatableSelect, { styles: mergedStyles, ref: innerRef, loadOptions: React.useMemo(() => debounceLoadOptions(loadOptions), [loadOptions]), ...commonProps, ...props, ...searchHintProps, isMulti: true, components: mergedComponents }));
};
export const StyledCreatableSelect = ({ components, innerRef, ...props }) => {
    const mergedComponents = useMergeComponents(components);
    const searchHintProps = useSearchHint(props);
    return (_jsx(SCreatableSelect, { styles: customStyles, ref: innerRef, ...commonProps, ...props, ...searchHintProps, components: mergedComponents }));
};
export const CreatableMultiSelect = ({ components, innerRef, ...restProps }) => {
    const mergedComponents = useMergeComponents(components);
    const searchHintProps = useSearchHint(restProps);
    const fixed = useFixedOptions(restProps);
    return (_jsx(SCreatableSelect, { styles: customStyles, ref: innerRef, ...commonProps, ...restProps, ...fixed, ...searchHintProps, isMulti: true, components: mergedComponents }));
};
export const AsyncSelect = ({ components, innerRef, loadOptions, styles, ...props }) => {
    const mergedComponents = useMergeComponents(components);
    const searchHintProps = useSearchHint(props);
    const mergedStyles = React.useMemo(() => mergeStyles(styles), [styles]);
    return (_jsx(SAsyncSelect, { styles: mergedStyles, ref: innerRef, loadOptions: React.useMemo(() => debounceLoadOptions(loadOptions), [loadOptions]), defaultOptions: true, ...commonProps, ...props, ...searchHintProps, components: mergedComponents }));
};
const multiProps = {
    isMulti: true,
    closeMenuOnSelect: false,
};
export const MultiSelect = ({ components, innerRef, formatOptionLabel, filterOption, ...restProps }) => {
    const mergedComponents = useMergeComponents(components);
    const searchHintProps = useSearchHint(restProps);
    const fixed = useFixedOptions(restProps);
    // Auto-generate customFilter for better search when formatOptionLabel is provided (same pattern as Select component)
    const customFilter = useCallbackRef((option, searchText) => matchesSelectFilter(option, searchText, formatOptionLabel));
    return (_jsx(StyledSelect, { styles: customStyles, ref: innerRef, formatOptionLabel: formatOptionLabel, filterOption: filterOption ||
            (formatOptionLabel ? (option, inputValue) => customFilter(option.data, inputValue) : undefined), ...multiProps, ...commonProps, ...restProps, ...fixed, ...searchHintProps, components: mergedComponents }));
};
export const AsyncMultiSelect = ({ components, innerRef, loadOptions, styles, ...props }) => {
    const mergedComponents = useMergeComponents(components);
    const searchHintProps = useSearchHint(props);
    const mergedStyles = React.useMemo(() => mergeStyles(styles), [styles]);
    return (_jsx(SAsyncSelect, { styles: mergedStyles, ref: innerRef, loadOptions: React.useMemo(() => debounceLoadOptions(loadOptions), [loadOptions]), defaultOptions: true, ...multiProps, ...commonProps, ...props, ...searchHintProps, components: mergedComponents }));
};
export { OptionMarkedLabel };
export { CustomMenuList, CustomOption } from './CustomComponents.js';
